/**
 * Sequencer store — PWA-side canonical library of step sequencers.
 *
 * Lives in localStorage under SEQUENCERS_KEY. Same model as macros: the PWA
 * owns the document, OPBridge holds a runtime snapshot. Two separate paths
 * carry it to OPBridge, not the legacy `bridge.saveSequencers(library)` this
 * comment used to describe (that Node bridge was deleted 2026-08-20):
 *   - Arm/play: `SequencerGrid.tsx`'s `driveArm` translates the definition
 *     into OPBridge's engine wire shape (port injection, camelCased fields)
 *     and pushes + arms it via `opbridge/dispatch.ts::pushSequencer`.
 *   - Across devices: each `project-<id>` sync doc carries the sequencers its
 *     items reference (`layout/projectSync.ts`, `layout/libraryMerge.ts`),
 *     newest `modifiedAt` wins per sequencer. Until 2026-09-28 it did not, and
 *     a sequencer authored on the laptop showed up EMPTY on the iPad.
 *
 * Layout items reference a sequencer by id; the sequencer itself is
 * reusable across pages and projects (drop one item on page 2 of
 * project A, drop another item referencing the same sequencerId on
 * page 1 of project B — both display + control the same pattern).
 */

import { createSignal, type Accessor } from 'solid-js';
import {
  type Sequencer,
  type SequencerLane,
  type SequencerLibrary,
  SEQUENCERS_SCHEMA_VERSION,
  blankStep,
} from '@opbuttons/schema';
import { ulid } from '../utils/ulid.ts';
import { createHistory } from '../undo/history.ts';
import { mergeLibraryEntries, stampChanged } from '../layout/libraryMerge.ts';

const SEQUENCERS_KEY = 'opbuttons-sequencers-v1';

function emptyLibrary(): SequencerLibrary {
  return { schemaVersion: SEQUENCERS_SCHEMA_VERSION, sequencers: [] };
}

/** Build a fresh lane with `lengthSteps` blank steps. Used when adding
 *  a new sequencer. */
export function makeBlankLane(
  label: string,
  note: number,
  channel = 0,
  lengthSteps = 16,
): SequencerLane {
  const steps = Array.from({ length: lengthSteps }, () => blankStep());
  return {
    id: ulid(),
    label,
    target: { bridge: 'pc', kind: 'noteon', channel, number: note },
    steps,
  };
}

/**
 * Build a fresh 4-lane drum sequencer. Defaults match a General MIDI
 * drum map (note 36 kick, 38 snare, 42 closed hat, 46 open hat) so the
 * first basic test (4 lanes of MIDI notes into Live) works without any
 * editor pass first. Channel 9 (0-indexed) is GM's drum channel.
 */
export function makeDrumSequencer(): Sequencer {
  return {
    id: ulid(),
    name: 'sequencer',
    lengthSteps: 16,
    stepsPerBeat: 4,  // 16 steps = 1 bar at 4/4
    lanes: [
      makeBlankLane('Kick',  36, 9),
      makeBlankLane('Snare', 38, 9),
      makeBlankLane('CHat',  42, 9),
      makeBlankLane('OHat',  46, 9),
    ],
  };
}

/** Read raw localStorage and coerce to current schema, migrating from
 *  earlier versions. Returns an empty library if anything looks wrong.
 *
 *  v1 → v3: `rows` → `lanes` + lane-level `lengthSteps` → sequencer-
 *  level `lengthSteps`. We collapse v1 and v2 into one migration since
 *  neither shipped beyond local-dev. */
function load(): SequencerLibrary {
  try {
    const raw = localStorage.getItem(SEQUENCERS_KEY);
    if (!raw) return emptyLibrary();
    const parsed = JSON.parse(raw) as { schemaVersion?: string; sequencers?: unknown[] };
    if (
      parsed.schemaVersion === 'opbuttons-sequencers/v1'
      || parsed.schemaVersion === 'opbuttons-sequencers/v2'
    ) {
      return migrateToV3(parsed);
    }
    if (parsed.schemaVersion !== SEQUENCERS_SCHEMA_VERSION) {
      console.warn(`sequencers: unknown schemaVersion ${parsed.schemaVersion}, starting empty`);
      return emptyLibrary();
    }
    return parsed as SequencerLibrary;
  } catch (err) {
    console.warn('sequencers: load failed', err);
    return emptyLibrary();
  }
}

/** Migrate v1 (`rows`, lane.lengthSteps) or v2 (`lanes`, lane.lengthSteps)
 *  to v3 (`lanes`, sequencer.lengthSteps). Pure function — exported for
 *  testability. */
export function migrateToV3(prev: { sequencers?: unknown[] }): SequencerLibrary {
  const seqs = Array.isArray(prev.sequencers) ? prev.sequencers : [];
  return {
    schemaVersion: SEQUENCERS_SCHEMA_VERSION,
    sequencers: seqs.map((raw) => {
      const s = raw as Record<string, unknown> & {
        rows?: Array<SequencerLane & { lengthSteps?: number }>;
        lanes?: Array<SequencerLane & { lengthSteps?: number }>;
        lengthSteps?: number;
      };
      const rawLanes = (s.lanes ?? s.rows ?? []) as Array<SequencerLane & { lengthSteps?: number }>;
      // Sequencer-level length: prefer existing, else max of any lane
      // length, else 16.
      const lengthSteps = typeof s.lengthSteps === 'number'
        ? s.lengthSteps
        : Math.max(16, ...rawLanes.map((l) => l.lengthSteps ?? 0));
      const lanes: SequencerLane[] = rawLanes.map((l) => {
        const { lengthSteps: _drop, ...rest } = l;
        return rest as SequencerLane;
      });
      const { rows: _r, ...rest } = s;
      return {
        ...rest,
        lanes,
        lengthSteps,
      } as unknown as Sequencer;
    }),
  };
}

function persist(lib: SequencerLibrary): void {
  try {
    localStorage.setItem(SEQUENCERS_KEY, JSON.stringify(lib));
  } catch (err) {
    console.warn('sequencers: persist failed', err);
  }
}

const [library, setLibrary] = createSignal<SequencerLibrary>(load());

export const sequencerLibrary: Accessor<SequencerLibrary> = library;
export const sequencers: Accessor<Sequencer[]> = () => library().sequencers;

/** Look up a sequencer by id, null if not found. */
export function findSequencer(id: string): Sequencer | null {
  return library().sequencers.find((s) => s.id === id) ?? null;
}

/** Write a locally-authored library: stamp `modifiedAt` on what changed, so
 *  project sync can tell this copy is newer than one on another device. */
function writeLocal(next: SequencerLibrary): void {
  const stamped = { ...next, sequencers: stampChanged(library().sequencers, next.sequencers, Date.now()) };
  setLibrary(stamped);
  persist(stamped);
}

/** Replace the entire library. Persists + emits a signal. */
export function setSequencerLibrary(next: SequencerLibrary): void {
  sequencerHistory.checkpoint();
  writeLocal(next);
}

/** History handle for the sequencer library. Covers add/update/remove
 *  sequencer, lane/step edits — everything through
 *  `setSequencerLibrary`. Undo and redo stamp too - see `stampChanged`. */
export const sequencerHistory = createHistory<SequencerLibrary>({
  name: 'sequencers',
  read: () => library(),
  write: (next) => writeLocal(next),
});

/** Adopt sequencers that project sync brought from another device, newer
 *  copy wins (`libraryMerge.ts`). NOT a local edit: no undo checkpoint and no
 *  re-stamp, so the sender's `modifiedAt` survives. Returns how many changed. */
export function applySyncedSequencers(incoming: readonly Sequencer[]): number {
  const cur = library();
  const merged = mergeLibraryEntries(cur.sequencers, incoming);
  if (!merged) return 0;
  const before = new Map(cur.sequencers.map((s) => [s.id, s]));
  const next = { ...cur, sequencers: merged };
  setLibrary(next);
  persist(next);
  return merged.filter((s) => before.get(s.id) !== s).length;
}

/** Insert or replace a sequencer by id. */
export function upsertSequencer(seq: Sequencer): void {
  const lib = library();
  const ix = lib.sequencers.findIndex((s) => s.id === seq.id);
  const next = ix === -1
    ? [...lib.sequencers, seq]
    : lib.sequencers.map((s, i) => (i === ix ? seq : s));
  setSequencerLibrary({ ...lib, sequencers: next });
}

/** Remove a sequencer by id. */
export function removeSequencer(seqId: string): void {
  const lib = library();
  setSequencerLibrary({
    ...lib,
    sequencers: lib.sequencers.filter((s) => s.id !== seqId),
  });
}

/**
 * Toggle a single step on/off. Idempotent for an already-correct state.
 * Persists immediately + the App's bridge sync hook will push the
 * updated library to the bridge on next save tick.
 */
export function toggleStep(seqId: string, laneId: string, stepIx: number): void {
  const lib = library();
  const seq = lib.sequencers.find((s) => s.id === seqId);
  if (!seq) return;
  const lane = seq.lanes.find((l) => l.id === laneId);
  if (!lane) return;
  if (stepIx < 0 || stepIx >= seq.lengthSteps) return;
  // Ensure the steps array is at least lengthSteps long (defensive).
  const steps = lane.steps.slice();
  while (steps.length < seq.lengthSteps) steps.push(blankStep());
  const cur = steps[stepIx] ?? blankStep();
  steps[stepIx] = { ...cur, on: !cur.on };
  setLaneSteps(seq.id, lane.id, steps);
}

/** Set a step's velocity (1..127). No-op if step doesn't exist. */
export function setStepVelocity(
  seqId: string,
  laneId: string,
  stepIx: number,
  velocity: number,
): void {
  const lib = library();
  const seq = lib.sequencers.find((s) => s.id === seqId);
  if (!seq) return;
  const lane = seq.lanes.find((l) => l.id === laneId);
  if (!lane) return;
  if (stepIx < 0 || stepIx >= seq.lengthSteps) return;
  const steps = lane.steps.slice();
  while (steps.length < seq.lengthSteps) steps.push(blankStep());
  const cur = steps[stepIx] ?? blankStep();
  const v = Math.max(1, Math.min(127, Math.round(velocity)));
  steps[stepIx] = { ...cur, velocity: v };
  setLaneSteps(seq.id, lane.id, steps);
}

/** Update a single lane's steps array. Internal helper for the toggle/
 *  velocity APIs and the lane editor. */
function setLaneSteps(seqId: string, laneId: string, steps: SequencerLane['steps']): void {
  const lib = library();
  setSequencerLibrary({
    ...lib,
    sequencers: lib.sequencers.map((s) => {
      if (s.id !== seqId) return s;
      return {
        ...s,
        lanes: s.lanes.map((l) => (l.id === laneId ? { ...l, steps } : l)),
      };
    }),
  });
}

/** Patch a lane's metadata (label, target, color). Length is no longer
 *  per-lane — see `patchSequencer` to change the sequencer's length. */
export function patchLane(
  seqId: string,
  laneId: string,
  patch: Partial<Omit<SequencerLane, 'id' | 'steps' | 'lengthSteps'>>,
): void {
  const lib = library();
  setSequencerLibrary({
    ...lib,
    sequencers: lib.sequencers.map((s) => {
      if (s.id !== seqId) return s;
      return {
        ...s,
        lanes: s.lanes.map((l) => {
          if (l.id !== laneId) return l;
          return { ...l, ...patch } satisfies SequencerLane;
        }),
      };
    }),
  });
}

/** Patch a sequencer's metadata (name, lengthSteps, stepsPerBeat).
 *  Length changes resize every lane's steps array, preserving existing
 *  values and padding new slots with blanks. */
export function patchSequencer(
  seqId: string,
  patch: Partial<Pick<Sequencer, 'name' | 'lengthSteps' | 'stepsPerBeat'>>,
): void {
  const lib = library();
  setSequencerLibrary({
    ...lib,
    sequencers: lib.sequencers.map((s) => {
      if (s.id !== seqId) return s;
      const next: Sequencer = {
        ...s,
        ...(patch.name !== undefined ? { name: patch.name.trim() || 'sequencer' } : {}),
        ...(patch.stepsPerBeat !== undefined ? { stepsPerBeat: patch.stepsPerBeat } : {}),
      };
      if (typeof patch.lengthSteps === 'number' && patch.lengthSteps !== s.lengthSteps) {
        const target = Math.max(1, Math.min(64, patch.lengthSteps | 0));
        next.lengthSteps = target;
        next.lanes = s.lanes.map((l) => {
          const existing = l.steps.slice(0, target);
          while (existing.length < target) existing.push(blankStep());
          return { ...l, steps: existing };
        });
      }
      return next;
    }),
  });
}

/** Rename a sequencer. Sugar over patchSequencer for callers that only
 *  care about the name. */
export function renameSequencer(seqId: string, name: string): void {
  patchSequencer(seqId, { name });
}
